import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Toastify,
  SpinnerLoader,
  Label,
  Input,
  Switch as LiftSwitch,
  PageHeader,
  DataTable,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Badge,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Plus, Trash2, RefreshCw, Edit, Search, X } from "lucide-react";
import apiClient from "@lift-admin/utils/apiClient";
import { formatIdWithName } from "@lift-admin/utils/logChangeFormatter";
import { logAdminAction } from "@lift-admin/utils/actionLogTrack";
import { useAuthContext } from "@lift-admin/utils/provider";

interface GA4Row {
  ga4Id: number | null;
  accountId: string;
  accountName: string;
  propertyId: string;
  propertyName: string;
  source: string;
  medValues: string[];
  singleMedValue: string;
  isExisting: boolean;
}

const EMPTY_ROW: GA4Row = {
  ga4Id: null,
  accountId: "",
  accountName: "",
  propertyId: "",
  propertyName: "",
  source: "",
  medValues: [],
  singleMedValue: "",
  isExisting: false,
};

const GA4Config = () => {
  const { canEdit } = useAuthContext();
  const hasEditAccess = canEdit("affiliate_mgmt");

  const user: any = JSON.parse(localStorage.getItem("user") || "{}");

  const [brands, setBrands] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [listLoad, setListLoad] = useState(true);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);

  const [affiliateAlias, setAffiliateAlias] = useState("");
  const [isGA4Active, setIsGA4Active] = useState(false);
  const [ga4Rows, setGa4Rows] = useState<GA4Row[]>([{ ...EMPTY_ROW }]);
  const [removedRows, setRemovedRows] = useState<GA4Row[]>([]);

  const [saving, setSaving] = useState(false);

  const fetchAffiliateBrands = useCallback(async () => {
    setListLoad(true);
    try {
      const res = await apiClient.get(
        `/brand/v1/admin/brands/allFields/${user.id}`
      );
      const allBrands = res.data?.response || [];
      const affiliateBrands = allBrands.filter((b: any) => b.is_affiliate);
      setBrands(affiliateBrands);
    } catch (err: any) {
      Toastify("error", err.response?.data?.message || "Failed to fetch brands");
    } finally {
      setListLoad(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchAffiliateBrands();
  }, [fetchAffiliateBrands]);

  const populateFormFromBrand = useCallback((brand: any) => {
    setRemovedRows([]);
    setAffiliateAlias(brand?.affiliate_alias || "");

    if (brand?.ga4Values) {
      const ga4Data = Array.isArray(brand.ga4Values)
        ? brand.ga4Values
        : [brand.ga4Values];

      const hasGA4Data = Array.isArray(brand.ga4Values)
        ? brand.ga4Values.length > 0
        : brand.ga4Values?.account_id ||
          brand.ga4Values?.account_name ||
          brand.ga4Values?.property_id ||
          brand.ga4Values?.property_name ||
          brand.ga4Values?.source;

      setIsGA4Active(!!hasGA4Data);

      if (hasGA4Data) {
        const loadedRows: GA4Row[] = ga4Data.map((ga4: any) => {
          const items = ga4?.medium?.split(",") || [];
          return {
            ga4Id: ga4?.id || null,
            accountId: ga4?.account_id?.toString() || "",
            accountName: ga4?.account_name || "",
            propertyId: ga4?.property_id?.toString() || "",
            propertyName: ga4?.property_name || "",
            source: ga4?.source || "",
            medValues: items.filter((item: string) => item.trim() !== ""),
            singleMedValue: "",
            isExisting: true,
          };
        });
        setGa4Rows(loadedRows);
      } else {
        setGa4Rows([{ ...EMPTY_ROW }]);
      }
    } else {
      setIsGA4Active(false);
      setGa4Rows([{ ...EMPTY_ROW }]);
    }
  }, []);

  const resetForm = () => {
    setAffiliateAlias("");
    setIsGA4Active(false);
    setGa4Rows([{ ...EMPTY_ROW }]);
    setRemovedRows([]);
  };

  const handleOpenEdit = (row: any) => {
    setSelectedBrand(row);
    populateFormFromBrand(row);
    setShowEditSheet(true);
  };

  const handleCloseEditSheet = () => {
    setShowEditSheet(false);
    setSelectedBrand(null);
    resetForm();
  };

  const addRow = () => {
    setGa4Rows((prev) => [...prev, { ...EMPTY_ROW }]);
  };

  const removeRow = (index: number) => {
    const row = ga4Rows[index];
    if (row.isExisting && row.ga4Id) {
      setRemovedRows((prev) => [...prev, row]);
    }
    setGa4Rows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof GA4Row, value: any) => {
    setGa4Rows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddMedium = (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const row = ga4Rows[rowIndex];
      if (row.singleMedValue.trim()) {
        setGa4Rows((prev) => {
          const updated = [...prev];
          updated[rowIndex] = {
            ...updated[rowIndex],
            medValues: [...updated[rowIndex].medValues, row.singleMedValue.trim()],
            singleMedValue: "",
          };
          return updated;
        });
      }
    }
  };

  const removeMedium = (rowIndex: number, mediumIndex: number) => {
    setGa4Rows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        medValues: updated[rowIndex].medValues.filter((_, i) => i !== mediumIndex),
      };
      return updated;
    });
  };

  const handleSave = async () => {
    if (!selectedBrand) return;

    if (
      isGA4Active &&
      ga4Rows.some((row) => !row.propertyId || row.propertyId.trim() === "")
    ) {
      Toastify("warning", "GA4 Property ID is required for all GA4 rows");
      return;
    }

    setSaving(true);
    try {
      const apiCalls: Promise<any>[] = [];

      const aliasFormData = new FormData();
      aliasFormData.append("affiliate_alias", affiliateAlias);
      apiCalls.push(
        apiClient.put(
          `/brand/v1/admin/brand/update/${selectedBrand.id}`,
          aliasFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      );

      if (isGA4Active) {
        const validRows = ga4Rows.filter(
          (row) => row.propertyId && row.propertyId.trim() !== ""
        );

        const existingRows = validRows.filter((row) => row.isExisting);
        const newRows = validRows.filter((row) => !row.isExisting);

        existingRows.forEach((row) => {
          const payload: any = {
            account_id: Number(row.accountId) || undefined,
            account_name: row.accountName || undefined,
            property_id: Number(row.propertyId),
            property_name: row.propertyName || undefined,
            source: row.source || undefined,
            medium: row.medValues.length > 0 ? row.medValues : undefined,
          };
          Object.keys(payload).forEach((key) => {
            if (payload[key] === undefined) delete payload[key];
          });

          apiCalls.push(
            apiClient.put(
              `/brand/v1/admin/update/ga4Values/${selectedBrand.id}`,
              payload,
              { headers: { "Content-Type": "application/json" } }
            )
          );
        });

        newRows.forEach((row) => {
          const payload: any = {
            account_id: Number(row.accountId) || undefined,
            account_name: row.accountName || undefined,
            property_id: Number(row.propertyId),
            property_name: row.propertyName || undefined,
            source: row.source || undefined,
            medium: row.medValues.length > 0 ? row.medValues : undefined,
          };
          Object.keys(payload).forEach((key) => {
            if (payload[key] === undefined) delete payload[key];
          });

          apiCalls.push(
            apiClient.post(
              `/brand/v1/admin/add/ga4Values/${selectedBrand.id}`,
              payload,
              { headers: { "Content-Type": "application/json" } }
            )
          );
        });
      }

      removedRows.forEach((row) => {
        if (row.ga4Id) {
          apiCalls.push(
            apiClient.delete(`/brand/v1/admin/ga4Values/${row.ga4Id}`)
          );
        }
      });

      await Promise.all(apiCalls);
      Toastify("success", "GA4 configuration saved successfully");
      
      const ga4ActiveRows = ga4Rows.filter((r) => r.propertyId?.trim());
      const ga4AddedCount = ga4ActiveRows.filter((r) => !r.isExisting).length;
      const ga4UpdatedCount = ga4ActiveRows.filter((r) => r.isExisting).length;
      const ga4RemovedCount = removedRows.filter((r) => r.ga4Id).length;

      // Build before -> after changes from the originally-loaded brand state.
      const originalAlias = (selectedBrand as any)?.affiliate_alias || '';
      const originalGA4Values = (selectedBrand as any)?.ga4Values;
      const originalGA4Active = Array.isArray(originalGA4Values)
        ? originalGA4Values.length > 0
        : !!(originalGA4Values && (originalGA4Values.account_id || originalGA4Values.property_id || originalGA4Values.property_name || originalGA4Values.source));
      const ga4Changes: Array<{ label: string; from?: string; to?: string }> = [];
      if (originalAlias !== affiliateAlias) {
        ga4Changes.push({ label: 'Affiliate Alias', from: originalAlias || 'Not Set', to: affiliateAlias || 'Not Set' });
      }
      if (originalGA4Active !== isGA4Active) {
        ga4Changes.push({ label: 'GA4 Tracking', from: originalGA4Active ? 'Enabled' : 'Disabled', to: isGA4Active ? 'Enabled' : 'Disabled' });
      }
      if (ga4AddedCount) ga4Changes.push({ label: 'GA4 Properties Added', from: '—', to: String(ga4AddedCount) });
      if (ga4UpdatedCount) ga4Changes.push({ label: 'GA4 Properties Updated', from: '—', to: String(ga4UpdatedCount) });
      if (ga4RemovedCount) ga4Changes.push({ label: 'GA4 Properties Removed', from: String(ga4RemovedCount), to: '—' });
      const ga4ChangeSummary = ga4Changes
        .map(c => `${c.label}: ${c.from} → ${c.to}`)
        .join('; ');
      await logAdminAction(
        `GA4 configuration updated for brand "${selectedBrand.name}"${ga4ChangeSummary ? ` — ${ga4ChangeSummary}` : ''}`,
        {
          url: `/brand/v1/admin/ga4Values/${selectedBrand.id}`,
          method: 'PUT',
          status: 200,
          category: 'settings',
          action: 'update_ga4_config',
          brandId: selectedBrand.id,
          brandName: selectedBrand.name,
          isGA4Active,
          rowCount: ga4ActiveRows.length,
          changes: ga4Changes,
          details: [
            { label: 'Brand ID', value: formatIdWithName(selectedBrand.id, selectedBrand.name) },
            { label: 'GA4 Tracking', value: isGA4Active ? 'Enabled' : 'Disabled' },
            { label: 'Affiliate Alias', value: affiliateAlias || 'Not Set' },
            { label: 'Rows Added', value: String(ga4AddedCount) },
            { label: 'Rows Updated', value: String(ga4UpdatedCount) },
            { label: 'Rows Removed', value: String(ga4RemovedCount) },
          ],
        }
      );
      
      setRemovedRows([]);
      setBrands((prev) =>
        prev.map((b) =>
          b.id === selectedBrand.id
            ? {
                ...b,
                affiliate_alias: affiliateAlias,
                ga4Values: isGA4Active
                  ? ga4Rows
                      .filter((r) => r.propertyId?.trim())
                      .map((r) => ({
                        id: r.ga4Id,
                        account_id: r.accountId,
                        account_name: r.accountName,
                        property_id: r.propertyId,
                        property_name: r.propertyName,
                        source: r.source,
                        medium: r.medValues?.length ? r.medValues.join(",") : undefined,
                      }))
                  : [],
              }
            : b
        )
      );
      setSelectedBrand((prev: any) =>
        prev
          ? {
              ...prev,
              affiliate_alias: affiliateAlias,
              ga4Values: isGA4Active
                ? ga4Rows.filter((r) => r.propertyId?.trim()).map((r) => ({
                    id: r.ga4Id,
                    account_id: r.accountId,
                    account_name: r.accountName,
                    property_id: r.propertyId,
                    property_name: r.propertyName,
                    source: r.source,
                    medium: r.medValues?.length ? r.medValues.join(",") : undefined,
                  }))
                : [],
            }
          : null
      );

      handleCloseEditSheet();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to save GA4 configuration";
      Toastify("error", errorMessage);
      await logAdminAction(
        `Failed to save GA4 config for brand ${selectedBrand?.name}: ${errorMessage}`,
        { category: 'settings' },
        true
      );
    } finally {
      setSaving(false);
    }
  };

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const filteredBrands = useMemo(() => {
    if (!searchText.trim()) return brands;
    const q = searchText.toLowerCase().trim();
    return brands.filter((b: any) => (b.name || "").toLowerCase().includes(q));
  }, [brands, searchText]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchText]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredBrands.slice(start, start + pageSize);
  }, [filteredBrands, pageIndex, pageSize]);

  const columns = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: "name",
        header: "Brand Name",
        accessor: "name",
        render: (_v: any, row: any) => (
          <span className="font-medium">{row.name}</span>
        ),
      },
    ];
    if (hasEditAccess) {
      cols.push({
        key: "actions",
        header: "Actions",
        accessor: (row: any) => row.id,
        width: "100px",
        align: "center" as const,
        render: (_v: any, row: any) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenEdit(row)}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
        ),
      });
    }
    return cols;
  }, [hasEditAccess]);

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title={`GA4 Configuration ${!searchText.trim() ? `(${brands.length})` : `(${filteredBrands.length} of ${brands.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAffiliateBrands()}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search affiliate brands..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={columns}
            data={paginatedData}
            loading={listLoad}
            emptyMessage={searchText ? "No affiliate brands match your search." : "No affiliate brands found. Only brands with Affiliate type are shown here."}
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: pageIndex + 1,
              totalPages: Math.max(1, Math.ceil(filteredBrands.length / pageSize)),
              pageSize,
              totalItems: filteredBrands.length,
              onPageChange: (p) => setPageIndex(p - 1),
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPageIndex(0);
              },
              pageSizeOptions: [25, 50, 100],
            }}
          />
        </div>
      </div>

      <Sheet open={showEditSheet} onOpenChange={(open) => !open && handleCloseEditSheet()}>
        <SheetContent className="flex flex-col h-full sm:max-w-xl p-0">
          <SheetHeader className="px-6 pt-6 pb-4 shrink-0">
            <SheetTitle>
              {selectedBrand ? `GA4 Config: ${selectedBrand.name}` : "GA4 Configuration" }
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6">
          {selectedBrand ? (
            <div className="flex flex-col gap-6 pt-4 pb-6">
              <div className="space-y-2">
                <Label>Affiliate Email Alias</Label>
                <Input
                  disabled={!hasEditAccess}
                  placeholder="e.g. lift@newengen.com"
                  value={affiliateAlias}
                  onChange={(e) => setAffiliateAlias(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-2">
                <Label className="font-normal">Want to link GA4?</Label>
                <LiftSwitch
                  checked={isGA4Active}
                  onCheckedChange={() => setIsGA4Active(!isGA4Active)}
                  disabled={!hasEditAccess}
                />
              </div>

              {isGA4Active && (
                <div className="flex flex-col gap-4">
                  {ga4Rows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex items-start gap-3 p-4 border rounded-lg bg-card"
                    >
                      <div className="grid grid-cols-1 gap-3 flex-1">
                        <div className="flex items-center justify-between w-full gap-3 min-h-8">
                          <h3 className="text-sm font-semibold text-foreground tracking-tight">
                            Configuration {rowIndex + 1}
                          </h3>
                          {hasEditAccess && ga4Rows.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive flex-shrink-0 h-8 w-8 p-0"
                              onClick={() => removeRow(rowIndex)}
                              aria-label="Delete GA4 configuration row"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Account Name</Label>
                          <Input
                            disabled={!hasEditAccess}
                            value={row.accountName}
                            onChange={(e) =>
                              updateRow(rowIndex, "accountName", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Account ID</Label>
                          <Input
                            type="number"
                            disabled={!hasEditAccess}
                            value={row.accountId}
                            onChange={(e) =>
                              updateRow(rowIndex, "accountId", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Property ID *</Label>
                          <Input
                            type="number"
                            disabled={!hasEditAccess}
                            value={row.propertyId}
                            onChange={(e) =>
                              updateRow(rowIndex, "propertyId", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Property Name</Label>
                          <Input
                            disabled={!hasEditAccess}
                            value={row.propertyName}
                            onChange={(e) =>
                              updateRow(rowIndex, "propertyName", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Source</Label>
                          <Input
                            disabled={!hasEditAccess}
                            value={row.source}
                            onChange={(e) =>
                              updateRow(rowIndex, "source", e.target.value)
                            }
                          />
                        </div>
                        <div className="mb-3 space-y-1.5">
                          <Label className="text-xs">Medium Tags</Label>
                          <div
                            className={[
                              "flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2",
                              !hasEditAccess ? "opacity-60 pointer-events-none" : "",
                            ].join(" ")}
                          >
                            {row.medValues.map((tag, medIdx) => (
                              <Badge
                                key={medIdx}
                                variant="secondary"
                                className="inline-flex items-center gap-1 pr-0.5 text-xs py-0.5"
                              >
                                {tag}
                                {hasEditAccess && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      removeMedium(rowIndex, medIdx);
                                    }}
                                    className="rounded-full p-0.5 hover:bg-muted-foreground/20 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                    aria-label="Remove tag"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </Badge>
                            ))}
                            <input
                              disabled={!hasEditAccess}
                              placeholder={row.medValues.length > 0 ? "" : "Type + Enter"}
                              value={row.singleMedValue}
                              onChange={(e) => updateRow(rowIndex, "singleMedValue", e.target.value)}
                              onKeyDown={(e) => handleAddMedium(e as any, rowIndex)}
                              className="flex-1 min-w-[160px] bg-transparent outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 text-xs placeholder:text-muted-foreground"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {hasEditAccess && (
                    <div className="flex justify-end">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={addRow}
                        className="w-fit bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add GA4 Row
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
          </div>

          <SheetFooter className="px-6 py-4 border-t shrink-0 mt-auto">
            <Button variant="outline" onClick={handleCloseEditSheet}>
              Cancel
            </Button>
            {hasEditAccess && selectedBrand && (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <SpinnerLoader />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default GA4Config;
